import { SpxSlider } from '@theme/spx-slider';

/** @typedef {ReturnType<SpxSlider['getConfig']> & { loopAdditionalSlides?: number }} EventLoopConfig */
/** @typedef {{ config: EventLoopConfig, container: HTMLElement }} EventSliderInitDetail */

class SpxEventCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  controller = null;
  /** @type {SpxSlider | null} */
  slider = null;
  /** @type {HTMLElement[]} */
  originalSlides = [];
  /** @type {ResizeObserver | null} */
  resizeObserver = null;
  /** @type {number | undefined} */
  pendingScene = undefined;
  loopPlanKey = 'static';
  initFrame = 0;
  loopFrame = 0;
  clipFrame = 0;
  /** @type {{ snap: number[], slides: number[] } | null} */
  loopGrids = null;

  connectedCallback() {
    if (this.controller) return;
    const slider = this.querySelector('spx-slider');
    if (!(slider instanceof SpxSlider)) return;
    this.slider = slider;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.originalSlides = [...this.querySelectorAll('.spx-event-carousel__slide:not([data-event-clone])')]
      .filter((slide) => slide instanceof HTMLElement)
      .sort((a, b) => Number(a.dataset.eventIndex) - Number(b.dataset.eventIndex));
    this.addEventListener('spx:before-slider-init', this.prepareLoop, { signal });
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleLoopCheck();
    });
    this.originalSlides.forEach((slide) => this.resizeObserver?.observe(slide));
    if (this.slider) this.resizeObserver.observe(this.slider);
    window.addEventListener('resize', this.scheduleLoopCheck, { signal });

    this.addEventListener('spx:slider-ready', this.handleSliderReady, { signal });
    this.addEventListener('spx:slide-change', this.syncPagination, { signal });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-event-page]');
      if (button instanceof HTMLElement) this.goToCard(Number(button.dataset.eventPage));
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const slide = event.target.closest('.spx-event-carousel__slide');
      if (!(slide instanceof HTMLElement) || slide.hasAttribute('data-event-clone')) return;
      const preview = () => {
        if (this.slider?.swiperInstance?.params.loop) this.slider.swiperInstance.autoplay?.stop();
        this.goToCard(Number(slide.dataset.eventIndex), 0, true);
      };
      if (this.slider?.swiperInstance) preview();
      else this.slider?.addEventListener('spx:slider-ready', preview, { once: true, signal });
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => {
      if (slider.swiperInstance?.params.loop && this.originalSlides.length > 1 && slider.dataset.autoplay === 'true' && slider.isInViewport) {
        slider.swiperInstance?.autoplay?.start();
      }
    }, { signal });
    if (this.slider?.hasAttribute('data-defer-init')) {
      this.slider.removeAttribute('data-defer-init');
      this.initFrame = requestAnimationFrame(() => {
        this.initFrame = 0;
        if (this.slider?.isConnected) this.slider.syncSwiperWithViewport();
      });
    } else {
      this.loopPlanKey = 'static';
      if (this.slider?.swiperInstance) this.handleSliderReady();
    }
  }

  /** @param {EventLoopConfig} config */
  getLoopPlan(config) {
    const count = this.originalSlides.length;
    if (!config.loop || count < 2) return { sets: 1, additional: 0, key: 'static' };
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const gap = desktop ? config.spaceBetweenDesktop : config.spaceBetweenMobile;
    const group = desktop ? config.slidesPerGroupDesktop : config.slidesPerGroupMobile;
    const container = this.slider?.querySelector('.swiper');
    const first = this.originalSlides[0];
    if (!(container instanceof HTMLElement) || !first) return { sets: 1, additional: 0, key: 'static' };
    const bounds = container.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const section = this.getBoundingClientRect();
    const visibleLeft = Math.max(0, section.left);
    const visibleRight = Math.min(viewportWidth, section.right);
    const spill = Math.max(0, bounds.left - visibleLeft, visibleRight - bounds.right);
    // The introduction card is twice as wide as image cards. Use the narrowest
    // original card to bound the maximum visible count at every loop position.
    const widths = this.originalSlides.map((slide) => slide.getBoundingClientRect().width).filter((width) => width > 0);
    const cardWidth = widths.length ? Math.min(...widths) : 280;
    const step = cardWidth + gap;
    const perView = ((container.clientWidth || viewportWidth) + gap) / step;
    // Reserve the cards visible outside the Swiper viewport, plus its move group.
    const additional = Math.max(1, Math.ceil(spill / step));
    const minimum = Math.ceil(perView) + 2 * (additional + group);
    const sets = Math.ceil(minimum / count);
    return { sets, additional, key: `${sets}:${additional}` };
  }

  /** @param {Event} event */
  prepareLoop = (event) => {
    if (!(event instanceof CustomEvent) || event.target !== this.slider) return;
    const { config, container } = /** @type {EventSliderInitDetail} */ (event.detail);
    const plan = this.getLoopPlan(config);
    this.loopPlanKey = plan.key;
    if (!config.loop || this.originalSlides.length < 2) return;
    const wrapper = container.querySelector('.swiper-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.querySelectorAll('[data-event-clone]').forEach((slide) => slide.remove());
    wrapper.append(...this.originalSlides);
    const copies = document.createDocumentFragment();
    for (let set = 1; set < plan.sets; set++) {
      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        if (!(clone instanceof HTMLElement)) return;
        clone.setAttribute('data-event-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        [clone, ...clone.querySelectorAll('*')].forEach((element) => {
          element.removeAttribute('id');
          [...element.attributes].forEach((attribute) => {
            if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
          });
        });
        copies.append(clone);
      });
    }
    wrapper.append(copies);
    config.loopAdditionalSlides = plan.additional;
    if (this.pendingScene !== undefined) config.initialSlide = this.pendingScene;
    this.loopPlanKey = plan.key;
  };

  handleSliderReady = () => {
    const swiper = this.slider?.swiperInstance;
    swiper?.off('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
    swiper?.off('beforeLoopFix', this.captureLoopGrids);
    swiper?.off('loopFix', this.syncLoopGrids);
    if (swiper?.params.loop) {
      swiper.on('beforeLoopFix', this.captureLoopGrids);
      swiper.on('loopFix', this.syncLoopGrids);
      swiper.on('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
      this.trackInitialClip();
    } else {
      cancelAnimationFrame(this.clipFrame);
      this.clipFrame = 0;
      this.slider?.removeAttribute('data-event-initial');
      this.slider?.style.removeProperty('--event-initial-clip');
    }
    this.pendingScene = undefined;
    this.syncPagination();
    this.scheduleLoopCheck();
  };

  captureLoopGrids = () => {
    const swiper = this.slider?.swiperInstance;
    if (swiper) this.loopGrids = { snap: swiper.snapGrid, slides: swiper.slidesGrid };
  };

  syncLoopGrids = () => {
    const swiper = this.slider?.swiperInstance;
    const grids = this.loopGrids;
    this.loopGrids = null;
    if (!swiper || !grids) return;
    // Swiper 12.0.3 slidePrev captures these arrays before loopFix. Mixed card
    // widths produce new grids during reordering; keep the captured arrays current.
    if (grids.snap !== swiper.snapGrid) grids.snap.splice(0, grids.snap.length, ...swiper.snapGrid);
    if (grids.slides !== swiper.slidesGrid) grids.slides.splice(0, grids.slides.length, ...swiper.slidesGrid);
  };

  trackInitialClip = () => {
    const slider = this.slider;
    if (this.clipFrame || !slider || !slider.swiperInstance?.params.loop || !slider.hasAttribute('data-event-initial')) return;
    this.clipFrame = requestAnimationFrame(this.updateInitialClip);
  };

  updateInitialClip = () => {
    this.clipFrame = 0;
    const slider = this.slider;
    const swiper = slider?.swiperInstance;
    const first = this.originalSlides[0];
    const container = slider?.querySelector('.swiper');
    if (!slider || !swiper?.params.loop || !first || !(container instanceof HTMLElement) || !slider.hasAttribute('data-event-initial')) return;
    const edge = first.getBoundingClientRect().left;
    const visibleLeft = Math.max(0, this.getBoundingClientRect().left);
    if (edge <= visibleLeft + 0.5) {
      // Its predecessor is now fully offscreen, so removing the mask reveals no card.
      slider.removeAttribute('data-event-initial');
      slider.style.removeProperty('--event-initial-clip');
      return;
    }
    const origin = container.getBoundingClientRect().left;
    slider.style.setProperty('--event-initial-clip', `${Math.min(0, edge - origin)}px`);
    // Follow the rendered position throughout both a drag and its settling transition.
    if (swiper.animating || swiper.touchEventsData?.isTouched) this.trackInitialClip();
  };

  rebuildLoop() {
    const slider = this.slider;
    const swiper = slider?.swiperInstance;
    if (!slider || !swiper) return;
    this.pendingScene = Number(swiper.slides[swiper.activeIndex]?.dataset.eventIndex) || 0;
    slider.destroySwiper();
    slider.initSwiper();
  }

  scheduleLoopCheck = () => {
    if (this.loopFrame) return;
    this.loopFrame = requestAnimationFrame(() => {
      this.loopFrame = 0;
      const slider = this.slider;
      if (!this.isConnected || !slider || !slider.swiperInstance) return;
      const plan = this.getLoopPlan(slider.getConfig());
      if (plan.key !== this.loopPlanKey) this.rebuildLoop();
    });
  };

  /**
   * @param {number} index
   * @param {number} [speed]
   * @param {boolean} [originalOnly]
   */
  goToCard(index, speed, originalOnly = false) {
    const swiper = this.slider?.swiperInstance;
    if (!swiper || !Number.isInteger(index)) return;
    const slides = /** @type {HTMLElement[]} */ (Array.from(swiper.slides));
    const candidates = slides.filter((slide) =>
      Number(slide.dataset.eventIndex) === index && (!originalOnly || !slide.hasAttribute('data-event-clone'))
    );
    if (candidates.length === 0) return;
    if (!swiper.params.loop) {
      const target = candidates[0];
      if (target) swiper.slideTo(slides.indexOf(target), speed);
      return;
    }

    // Select the nearest occurrence, including across the loop boundary.
    const count = slides.length;
    /** @param {HTMLElement} slide */
    const distance = (slide) => {
      const delta = Math.abs(Number(slide.dataset.swiperSlideIndex) - swiper.realIndex);
      return Math.min(delta, count - delta);
    };
    candidates.sort((a, b) => distance(a) - distance(b));
    const target = candidates[0];
    if (target) swiper.slideToLoop(Number(target.dataset.swiperSlideIndex), speed);
  }

  syncPagination = () => {
    const swiper = this.slider?.swiperInstance;
    if (!swiper) return;
    const index = Number(swiper.slides[swiper.activeIndex]?.dataset.eventIndex);
    this.querySelectorAll('[data-event-page]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const active = Number(button.dataset.eventPage) === index;
      button.classList.toggle('swiper-pagination-bullet-active', active);
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  };

  disconnectedCallback() {
    this.slider?.swiperInstance?.off('beforeLoopFix', this.captureLoopGrids);
    this.slider?.swiperInstance?.off('loopFix', this.syncLoopGrids);
    this.loopGrids = null;
    this.slider?.swiperInstance?.off('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.loopFrame);
    this.loopFrame = 0;
    cancelAnimationFrame(this.initFrame);
    this.initFrame = 0;
    cancelAnimationFrame(this.clipFrame);
    this.clipFrame = 0;
  }
}

if (!customElements.get('spx-event-carousel')) {
  customElements.define('spx-event-carousel', SpxEventCarousel);
}
